import { defineComponent as _defineComponent } from 'vue'
import type { Area, Category, Keyword, Language } from '@ilteducation/content-ref-data-types';
import { computed, ref, watch } from 'vue';
import { NewVideoV1, UploadedFile, VideoMarket, VideoV1 } from '@ilteducation/content-registry-types';
import slugify from 'slugify';
import { VueEditor } from 'vue2-editor/dist/vue2-editor.esm';
import { getAllRefData } from '@/data-provider/refDataProvider';
import DropFile from '@/components/base/DropFile.vue';
import VideoUploader from '@/components/videos/VideoUploader.vue';
import VideoSubjectAreaConnector from '@/components/videos/VideoSubjectAreaConnector.vue';
import useUploadMedia from '@/comp/use-upload-media';
import admin from '@/plugins/admin';
import eventBus from '@/foundations/services/eventBus';
import DisableableButton from '@/components/buttons/DisableableButton.vue';
import { getTitle } from '@/foundations/helpers/videoUtils';
import SubtitlesAndNarrations from '@/resources/videos/form/SubtitlesAndNarrations.vue';
import Slug from '@/resources/videos/form/Slug.vue';
import Keywords from '@/resources/videos/form/Keywords.vue';
import useMediaFormFeature from '@/comp/use-media-form-feature';

type Props = {
  id?: string;
  title: string;
  isCreate?: boolean;
  item?: VideoV1;
};

type NewVideoV1Required = Required<Omit<NewVideoV1, 'state' | 'videoFormat'>>;


export default /*#__PURE__*/_defineComponent({
  __name: 'Form',
  props: {
    id: { default: undefined },
    title: null,
    isCreate: { type: Boolean, default: false },
    item: { default: undefined }
  },
  setup(__props: any) {

const props = __props as { id: string, title: string, isCreate: boolean, item: VideoV1 }

/* eslint-disable no-restricted-syntax */
const emptyVideo: NewVideoV1Required = {
  type: 'VideoV1',
  realm: 'all',
  availabilityRealms: [],
  description: [],
  slug: {},
  slugHistory: {},
  filmId: '',
  title: [],
  introfilmsIds: {},
  introfilmsUids: {},
  summary: [],
  bookLinks: {},
  narrations: [],
  previewImage: {},
  subtitles: [],
  orderedAreas: [],
  categories: [],
  keywords: [],
  keywordsLearnIt: [],
  videos: [],
  files: {},
  grades: {},
};


const cdnDomain = process.env.VUE_APP_COVERS_DOMAIN;
const isFormEdited = ref(false);

const availableManabuaKeywords = ref<Keyword[]>();
const availableLearnItKeywords = ref<Keyword[]>();
const loadingKeywords = ref(true);
getAllRefData('keywords').then((allKeywords: Keyword[]) => {
  availableManabuaKeywords.value = allKeywords.filter((k) => (k.applications || []).includes('manabua'));
  availableLearnItKeywords.value = allKeywords.filter((k) => (k.applications || []).includes('learn-it'));
  loadingKeywords.value = false;
});

const availableAreas = ref<Area[]>([]);
getAllRefData('areas').then((areas) => {
  availableAreas.value = areas;
});

const availableCategories = ref<Category[]>([]);
getAllRefData('categories').then((categories: Category[]) => {
  availableCategories.value = categories.filter((c) => c.subscriptionTypes?.some((t) => t.startsWith('immigrant')));
});

const languages = ref<Language[]>([]);
getAllRefData('languages').then((langs: Language[]) => {
  languages.value = langs;
});

function languageNameFromCode(code: string) {
  return languages.value.find((l) => l.languageCode === code)?.language;
}

function categoryNameFromId(categoryId: string) {
  return availableCategories.value.find((c) => c.id === categoryId)?.category;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/naming-convention
const { id: _id, createdAt, lastUpdatedAt, state, ...editableVideo } = { ...emptyVideo, ...props.item };
const DefaultItem = ref<Required<Omit<NewVideoV1, 'state' | 'videoFormat'>>>(
  props.isCreate ? { ...emptyVideo } : { ...editableVideo },
);

function getVideosForMarket(market: VideoMarket) {
  return props.item?.videos
    ?.filter((v) => v.market === market)
    .map((v) => ({
      category: availableCategories.value.find((c) => c.id === v.categoryId),
      area: availableAreas.value.find((ar) => ar.id === v.areaId),
      fileId: v.fileId,
    }))
    .filter((ac): ac is { category: Category; area: Area; fileId: string } => ac.category !== undefined);
}

function getFilesAsArray() {
  if (!props.item?.files) {
    return [];
  }
  return Object.entries(props.item?.files).map(([key, value]) => ({
    id: key,
    ...value,
  }));
}

function videoUrl(market: VideoMarket, category: Category, area: Area) {
  return `https://${process.env.VUE_APP_ENVIRONMENT === 'production' ? 'app' : 'app-dev'}.${
    market === 'se' ? 'begreppa' : 'manabua'
  }.${market}/videos/${category.slug}/${area.slug}/${props.item?.slug[market]}`;
}

const markets: { name: string; code: VideoMarket; lang: string }[] = [
  {
    name: 'Sweden',
    code: 'se',
    lang: 'sv',
  },
  {
    name: 'Finland',
    code: 'fi',
    lang: 'fi',
  },
  {
    name: 'Norway',
    code: 'no',
    lang: 'nb',
  },
];

const tab = ref(0);
const showPreviewImage = ref<boolean>(false);

const editTitle = (text: string, lang: string) => {
  const title = DefaultItem.value.title.find((t) => t.languageCode === lang);
  if (title) {
    title.value = text;
    // generate slug if no slug present
    const targetMarket = markets.find((m) => m.lang === lang)?.code;
    if (targetMarket) {
      DefaultItem.value.slug[targetMarket] = slugify(
        text
          .replace(/[!.:_()<>"#€%&\/=+~^'*]/gi, '')
          .toLowerCase()
          .trim(),
      );
    }
  } else {
    DefaultItem.value.title.push({ market: 'global', languageCode: lang, value: text });
  }
};

const editDescription = (text: string, lang: string) => {
  const description = DefaultItem.value.description.find((t) => t.languageCode === lang);
  if (description) {
    description.value = text;
  } else {
    DefaultItem.value.description.push({ market: 'global', languageCode: lang, value: text });
  }
};

const editSummary = (text: string, lang: string) => {
  const summaryForLang = DefaultItem.value.summary.find((t) => t.languageCode === lang);
  if (summaryForLang) {
    summaryForLang.value = text;
  } else {
    DefaultItem.value.summary.push({ market: 'global', languageCode: lang, value: text });
  }
};

const gradeOptionsForMarket = (market: VideoMarket) => {
  return availableCategories.value.filter((c) => c.kind === 'grade' && c.realms?.includes(market));
};

const newBooklinkForm = ref<{ category: string; language: string; bookId: string; url?: string }>({
  category: '',
  language: '',
  bookId: '',
  url: undefined,
});

const formRef = ref();

const reloadPage = () => {
  setTimeout(() => {
    window.location.reload();
  }, 1000);
  // reload the page to show the newly uploaded file
};

const connectVideoToCategoryAndArea = async (
  area: Area['id'],
  category: Category['id'],
  file: UploadedFile['fileId'],
  market: string,
) => {
  // Check if connection already exists
  if (
    DefaultItem.value.videos.find(
      (v) => v.market === market && v.categoryId === category && v.areaId === area && v.fileId === file,
    )
  ) {
    eventBus.$emit('snackbar.open', 'Video already exists for this subject and area in this market.');
    return;
  }
  if (!file) {
    eventBus.$emit('snackbar.open', 'No file selected');
    return;
  }
  // Add video to videos
  if (!DefaultItem.value.videos) {
    DefaultItem.value.videos = [];
  }
  DefaultItem.value.videos.push({
    market: market as VideoMarket,
    categoryId: category,
    areaId: area,
    fileId: file,
  });
  await formRef.value.save();
  reloadPage();
};

const isVideoV1 = (item: VideoV1 | NewVideoV1): item is VideoV1 => item.type === 'VideoV1' && 'id' in item && !!item.id;
const { upload, uploadInProgress } = useUploadMedia();
const selectedPreviewImage = ref<File>();
const handlePreviewFileSelection = (files: File[]) => {
  if (!props.item || !isVideoV1(props.item)) {
    return;
  }
  if (files.length) {
    selectedPreviewImage.value = files[0];
  } else {
    selectedPreviewImage.value = undefined;
  }
};
const uploadPreview = async (market: string) => {
  if (!selectedPreviewImage.value || !props.item || !isVideoV1(props.item)) {
    return;
  }
  await upload(props.item.id, selectedPreviewImage.value!, { subType: 'preview', market });
  selectedPreviewImage.value = undefined;
  reloadPage();
};
const removeFromVideos = async (market: string, categoryId: string, areaId: string, fileId: string) => {
  const { item } = props;

  if (!item || !isVideoV1(item)) {
    return;
  }

  if (!(await admin.confirm('Are you sure you want to remove this video from the subject/area?'))) {
    return;
  }

  const videoToRemove = item.videos?.find(
    (v) => v.market === market && v.fileId === fileId && v.categoryId === categoryId && v.areaId === areaId,
  );
  if (!videoToRemove) {
    return;
  }
  DefaultItem.value.videos = DefaultItem.value.videos.filter((v) => v !== videoToRemove);

  await formRef.value.save();
  reloadPage();
};

const rules = {
  notEmptyRule: (val?: string) => (val || '').length > 0 || 'This field is required',
};

const richTextEditorToolbar = [
  [{ header: [false, 1, 2, 3] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
];

const isRealmIncluded = (realm: string) => DefaultItem.value.availabilityRealms.includes(realm) ?? false;

const previousValue = ref<{ [market: string]: boolean }>({
  sv: isRealmIncluded('se'),
  fi: isRealmIncluded('fi'),
  nb: isRealmIncluded('no'),
});

const toggledActive = computed<{ [market: string]: boolean }>(() => ({
  sv: isRealmIncluded('se'),
  fi: isRealmIncluded('fi'),
  nb: isRealmIncluded('no'),
}));

const missingFields = ref<string[]>([]);
const showWarning = computed(() => missingFields.value.length > 0);

const checkFields = () => {
  missingFields.value = [];
  const { lang, code } = markets[tab.value];
  if (toggledActive.value[lang] && !previousValue.value[lang]) {
    const itemValue = DefaultItem.value;
    if (!itemValue.title.find((t) => t.languageCode === lang)?.value) {
      missingFields.value.push('Title');
    }
    if (!itemValue.slug[code]) {
      missingFields.value.push('Slug');
    }
    if (!(props.item?.previewImage[code] || props.item?.previewImage.all)) {
      missingFields.value.push('Preview Image');
    }
    if (!itemValue.videos?.find((v) => v.market === code)) {
      missingFields.value.push('Videos');
    }
  }
  previousValue.value = toggledActive.value;
};

const previewInMarket = (market: VideoMarket) => {
  return `https://${cdnDomain}/public/videos/${props.item?.id}/${
    props.item?.previewImage[market]?.fileName ?? props.item?.previewImage.all?.fileName
  }`;
};

const { getStateColor } = useMediaFormFeature(props.item);

watch(DefaultItem.value, () => {
  isFormEdited.value = true;
});

return { __sfc: true,emptyVideo, props, cdnDomain, isFormEdited, availableManabuaKeywords, availableLearnItKeywords, loadingKeywords, availableAreas, availableCategories, languages, languageNameFromCode, categoryNameFromId, _id, createdAt, lastUpdatedAt, state, editableVideo, DefaultItem, getVideosForMarket, getFilesAsArray, videoUrl, markets, tab, showPreviewImage, editTitle, editDescription, editSummary, gradeOptionsForMarket, newBooklinkForm, formRef, reloadPage, connectVideoToCategoryAndArea, isVideoV1, upload, uploadInProgress, selectedPreviewImage, handlePreviewFileSelection, uploadPreview, removeFromVideos, rules, richTextEditorToolbar, isRealmIncluded, previousValue, toggledActive, missingFields, showWarning, checkFields, previewInMarket, getStateColor, VueEditor, DropFile, VideoUploader, VideoSubjectAreaConnector, DisableableButton, getTitle, SubtitlesAndNarrations, Slug, Keywords }
}

})